"use client";

import { usePathname } from "next/navigation";
import { useState, useEffect, Suspense } from "react";
import { createClient } from "@/lib/supabase/client";
import { Sidebar } from "./sidebar";
import { BottomTabBar } from "./bottom-tab-bar";
import { CommandPalette } from "./command-palette";
import { refreshPasskeyVerification } from "@/app/auth/passkey/actions";

export interface ModuleFlags {
  intermittenceEnabled: boolean;
  portefeuilleEnabled: boolean;
  // LOT 248 — Module "Groupe d'amis" (custom label personnalisable).
  groupeAmisEnabled: boolean;
  groupeAmisLabel: string;
  /** Gestic Light — module d'analyse « Répartition des dépenses ». */
  repartitionEnabled?: boolean;
}

// Cache module-level du check whitelist (une seule requête par session).
let whitelistChecked = false;
let whitelistOk = true;

const DEFAULT_FLAGS: ModuleFlags = {
  intermittenceEnabled: false,
  portefeuilleEnabled: false,
  groupeAmisEnabled: false,
  groupeAmisLabel: "Groupe d'amis",
  repartitionEnabled: false,
};
/** Les flags sont mémorisés : sans ça, chaque chargement repart de « tout
 *  désactivé » et les modules optionnels apparaissent une demi-seconde après
 *  les autres (voire pas du tout si la requête échoue). Le réseau ne fait plus
 *  que confirmer/corriger ce qu'on affiche déjà. */
const FLAGS_CACHE_KEY = "gestic-module-flags";
// Cache module-level : survit aux remounts du shell dans une même session.
let flagsCache: ModuleFlags | null = null;
function readFlagsCache(): ModuleFlags | null {
  if (flagsCache) return flagsCache;
  try {
    const raw = localStorage.getItem(FLAGS_CACHE_KEY);
    if (!raw) return null;
    flagsCache = { ...DEFAULT_FLAGS, ...(JSON.parse(raw) as Partial<ModuleFlags>) };
    return flagsCache;
  } catch { return null; }
}
function writeFlagsCache(f: ModuleFlags) {
  flagsCache = f;
  try { localStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify(f)); } catch { /* noop */ }
}

function isPublicShellRoute(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname === "/login" ||
    pathname === "/blocked" ||
    pathname === "/merci" ||
    pathname.startsWith("/legal/") ||
    pathname.startsWith("/auth") ||
    pathname.startsWith("/rando/share/") ||
    pathname.startsWith("/amis/share/") ||
    pathname.startsWith("/famille/share/") ||
    pathname.startsWith("/famille/miroir/") ||
    // Module Dispos : lien personnel d'un participant sans compte. Lui
    // afficher la navigation de l'app n'aurait aucun sens — il n'a accès
    // à rien d'autre qu'à cette page.
    pathname.startsWith("/dispos/")
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // Garde-fou client : si l'utilisateur est authentifié mais PAS dans la
  // whitelist (allowed_users), on ne rend RIEN de l'app et on le renvoie
  // vers /blocked. Défense en profondeur au-dessus du proxy serveur (au cas
  // où un soft-nav aurait laissé passer). Erreur transitoire = on ne bloque
  // pas (le proxy reste l'autorité).
  const [accessBlocked, setAccessBlocked] = useState(false);
  useEffect(() => {
    if (isPublicShellRoute(pathname)) return;
    if (whitelistChecked) {
      if (!whitelistOk) setAccessBlocked(true);
      return;
    }
    const supabase = createClient();
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user?.email) return; // pas connecté → le proxy/login gèrent
      const { data, error } = await supabase
        .from("allowed_users")
        .select("email")
        .eq("email", user.email.toLowerCase())
        .maybeSingle();
      if (error) return; // transitoire → ne bloque pas
      whitelistChecked = true;
      whitelistOk = !!data;
      if (!data) {
        setAccessBlocked(true);
        window.location.assign("/blocked");
      }
    });
  }, [pathname]);
  // Démarre sur les flags mémorisés (cf. readFlagsCache) → les modules
  // optionnels sont dans la nav dès le premier rendu, comme les autres.
  const [moduleFlags, setModuleFlags] = useState<ModuleFlags>(DEFAULT_FLAGS);
  useEffect(() => {
    // Lecture après le premier rendu (pas d'accès localStorage pendant le
    // rendu serveur). L'identité DEFAULT_FLAGS sert de garde : si la base a
    // déjà répondu, on ne réécrase pas sa réponse avec le cache.
    queueMicrotask(() => {
      const cached = readFlagsCache();
      if (cached) setModuleFlags((prev) => (prev === DEFAULT_FLAGS ? cached : prev));
    });
  }, []);

  // Rafraîchit les flags depuis la base. Chargé sur TOUTES les pages de
  // l'app : la nav qui les consomme est globale, la gater sur /budget
  // faisait disparaître le module dès qu'on était ailleurs.
  useEffect(() => {
    if (isPublicShellRoute(pathname)) return;
    const supabase = createClient();
    const load = () => {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (!user) return;
        // Chaque requête est isolée : une table absente ou une erreur
        // transitoire ne doit pas faire retomber les autres flags à false
        // (c'est ce qui faisait disparaître le module par intermittence).
        type Row = { enabled?: boolean | null; custom_label?: string | null } | null;
        const safe = (p: PromiseLike<{ data: unknown }>): Promise<Row> =>
          Promise.resolve(p).then((r) => (r.data ?? null) as Row, () => null);
        Promise.all([
          safe(supabase.from("intermittence_settings").select("enabled").eq("user_id", user.id).maybeSingle()),
          safe(supabase.from("portfolio_settings").select("enabled").eq("user_id", user.id).maybeSingle()),
          safe(supabase.from("groupes_amis_settings").select("enabled, custom_label").eq("user_id", user.id).maybeSingle()),
          safe(supabase.from("light_repartition_settings").select("enabled").eq("user_id", user.id).maybeSingle()),
        ]).then(([im, po, ga, re]) => {
          setModuleFlags((prev) => {
            const next: ModuleFlags = {
              // `?? prev` et non `?? false` : une requête qui échoue laisse le
              // flag tel quel au lieu de masquer un module réellement actif.
              intermittenceEnabled: im?.enabled ?? prev.intermittenceEnabled,
              portefeuilleEnabled: po?.enabled ?? prev.portefeuilleEnabled,
              groupeAmisEnabled: ga?.enabled ?? prev.groupeAmisEnabled,
              groupeAmisLabel: ga?.custom_label || prev.groupeAmisLabel,
              repartitionEnabled: re?.enabled ?? prev.repartitionEnabled,
            };
            writeFlagsCache(next);
            return next;
          });
        });
      });
    };
    load();
    // Un toggle dans les Réglages (Pro ou Light) émet cet event → la nav
    // (sidebar + bottom bar) se met à jour IMMÉDIATEMENT, sans navigation.
    const onFlags = () => load();
    window.addEventListener("gestic:module-flags-changed", onFlags);
    return () => window.removeEventListener("gestic:module-flags-changed", onFlags);
  // Un seul chargement par session d'app : la nav est globale, inutile de
  // re-tirer les réglages à chaque navigation (l'event couvre les toggles).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicShellRoute(pathname)]);

  // LOT 130 — Met à jour `last_seen_at` + flippe status invited→accepted
  // au premier login. Une fois par session (au mount du shell), pas à
  // chaque navigation. Fire-and-forget : si la RPC n'existe pas encore
  // (migration non appliquée), on ignore l'erreur silencieusement.
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      // RPC retourne void, l'erreur arrive via { error } au lieu d'un rejection.
      supabase.rpc("mark_user_seen").then(() => { /* noop, on ignore aussi l'erreur si la migration n'est pas appliquée */ });
    });
    // Glisse la fenêtre du cookie passkey (rolling) → un utilisateur actif
    // n'est jamais re-sollicité. Fire-and-forget, no-op si pas de cookie valide.
    void refreshPasskeyVerification();
  }, []);

  // Don't show shell on landing / login / blocked / auth pages
  // LOT 226 — Landing page publique sans shell (sidebar, bottom bar, etc.)
  // Module Rando : la page de partage public `/rando/share/[token]`
  // est aussi sans shell — accessible aux non-users.
  // La liste était recopiée ici : y ajouter une page publique sans penser à
  // l'autre laissait la barre de l'app s'afficher devant un visiteur sans
  // compte. Une seule source, donc.
  if (
    isPublicShellRoute(pathname) ||
    // Vue imprimable d'une setlist (PDF) : plein écran, sans shell.
    (pathname.startsWith("/music/setlists/") && pathname.endsWith("/print"))
  ) {
    return <>{children}</>;
  }

  // Compte authentifié mais non autorisé → on ne montre RIEN de l'app
  // (ni sidebar, ni modules, ni contenu) le temps de partir vers /blocked.
  if (accessBlocked) return null;

  // LOT 217 — 3ème module : Arbre généalogique (/famille)
  // Module Rando (/rando) — gear lists, randos, groupes
  const activeModule = pathname.startsWith("/music") ? "music"
    : pathname.startsWith("/famille") ? "famille"
    : pathname.startsWith("/rando") ? "rando"
    : pathname.startsWith("/light") ? "light"
    : pathname.startsWith("/voiture") ? "voiture"
    : pathname.startsWith("/velo") ? "velo"
    : pathname.startsWith("/dispos") ? "dispos"
    : pathname.startsWith("/sante") ? "sante"
    : "budget";

  return (
    <div className="min-h-dvh bg-background">
      {/* Desktop sidebar */}
      {/* Suspense obligatoire : la sidebar lit les paramètres d'URL
          (?groupe= du module Dispos) via useSearchParams, ce qui force le
          rendu client. Sans cette limite, le prérendu statique de
          /_not-found échouait et cassait tout le build. Le repli reprend
          la largeur exacte de la sidebar pour éviter un saut de mise en
          page pendant l'hydratation. */}
      <Suspense fallback={<aside className="hidden md:block fixed inset-y-0 left-0 w-60 bg-surface border-r border-border-default z-40" />}>
        <Sidebar activeModule={activeModule} moduleFlags={moduleFlags} />
      </Suspense>

      {/* Main content */}
      {activeModule === "famille" ? (
        // LOT 217 — Famille = full-bleed (canvas pan/zoom), pas de
        // wrapper standard (max-w + padding).
        <>{children}</>
      ) : (
        <main className="md:pl-60 pb-24 md:pb-0 min-h-dvh">
          <div className="mx-auto max-w-5xl px-4 md:px-8 py-6 md:py-8">
            {/* Nav mobile (module switch + onglets + cloche) = pastille
                flottante du BottomTabBar. Plus de header mobile par module. */}
            {children}
          </div>
        </main>
      )}

      {/* Mobile bottom tab bar */}
      <BottomTabBar activeModule={activeModule} moduleFlags={moduleFlags} />

      {/* REFONTE v2 — Palette de commandes ⌘K (ne rend rien tant que fermée) */}
      <CommandPalette activeModule={activeModule} moduleFlags={moduleFlags} />
    </div>
  );
}
